import ErrorBoundary from 'app/components/ErrorBoundary';
import SecondaryTable from 'app/components/Tables/SecondaryTable';
import { _t } from 'app/utils/i18n';
import { path } from 'app/utils/path';
import { apiRoutes } from 'videos/utils/apiRoutes';
import React, { Fragment, FunctionComponent } from 'react';
import {
  baseLineData,
  channelIconCard,
  channelTopVideosColumn,
  relatedEntitiesColumns,
  relatedInfluencersColumns,
  trendingListColumns,
  videoIconCards,
} from '../Dashboard/VideosCharts';
import MostFrequentCard from 'app/components/MostFrequentCard';
import { Grid } from '@material-ui/core';
import BaseLine from 'app/components/Charts/BaseLine';
import CardTitle from 'app/components/CardTitle';
import IconCard from 'app/components/IconCard';
import styles from './VideoView.module.scss';
import VideoDetails from 'app/components/VideoDetails';
import { dateTimeFormatter } from 'app/utils/dateTimeFormatter';
import { checkHoveringTranslated, checkTranslated } from 'app/utils/helpers';
import ToolTipComp from 'app/components/ToolTipComp';
import AddComment from 'reports/components/AddComment';
import Paragraph from 'reports/components/Paragraph';
import { useReport } from 'reports/pages/AddNewReport/AddNewReport';
import { useQuery } from 'react-query';
import { getRelatedTags, getTrendingListVideo } from 'videos/Requests';
import { handleLongString } from 'app/utils/stringMethods';
import ItemPopperCell from 'app/components/Tables/Cells/ItemPoperCell';
import { getLowestCountry } from 'app/utils/getLowestCountry';

interface Props {
  videoId: number;
  currentVideo: any;
  handelAuthorizationOnClick?: any;
  isReport?: boolean;
  isTemplate?: boolean;
  selectedSlugs?: any;
  removeComments?: boolean;
  isTrending?: boolean;
  openModalToAddinfluencer?: any;
}
export const VideoViewCharts: FunctionComponent<Props> = ({
  videoId,
  currentVideo,
  handelAuthorizationOnClick,
  isReport,
  isTemplate,
  selectedSlugs,
  removeComments,
  isTrending,
  openModalToAddinfluencer,
}) => {
  const checkIsReport = (report: any, notReport: any = undefined) =>
    isReport ? report : notReport;
  const report = useReport();
  const newTrendingListColumns = [...trendingListColumns];
  newTrendingListColumns
    .splice(2, 1, {
      title: 'country',
      key: 'countries',
      render: (data: any) => (
        <ItemPopperCell
          data={data?.slice(0, 5)?.map((item: any) => ({
            ...item,
            title: item.country_name,
            count: item.mentions,
          }))}
          headers={[_t('basic:countries'), _t('basic:rank')]}
          // text={getHighestCountry(data)?.name || ''}
          text={getLowestCountry(data)?.name || ''}
        />
      ),
    })
    ?.slice(0, 5);
  const influencerDetails = [
    {
      title:
        currentVideo?.influencer?.is_entity === false
          ? _t('videos:influencer_name')
          : _t('videos:entity_name'),
      key: 'name',
    },
    {
      title: _t('videos:joined'),
      key: 'joined_at',
    },
    {
      title: _t('videos:platform'),
      key: 'platform',
    },
    {
      title: _t('videos:country'),
      key: 'address',
    },
  ];
  const { data: relatedTags } = useQuery(
    ['getRelatedTags', videoId],
    getRelatedTags,
  );
  const { data: trendingListVideo } = useQuery(
    ['getTrendingListVideo', videoId],
    getTrendingListVideo,
  );
  const charts = [
    {
      id: 'videos_video_view_video_details',
      component: (
        <Grid item sm={12} className={styles.mb}>
          <ErrorBoundary>
            <CardTitle
              title={_t('trends:video_details')}
              showBorderBottom
              hideIcon
              maxFontSize
              compId="videos_video_view_video_details"
            />
          </ErrorBoundary>
        </Grid>
      ),
    },
    ...videoIconCards?.map(({ title, key, icon, id }) => ({
      id: `videos_video_view_${id}`,
      component: (
        <Grid item {...checkIsReport({ md: 3, lg: 6 }, { sm: 12, md: 3 })}>
          <ErrorBoundary key={key}>
            <IconCard
              key={key}
              title={title}
              withFilter
              hideInput
              isReport={isReport}
              cardData={{
                count: currentVideo?.[key],
                icon: icon,
              }}
              compId={`videos_video_view_${id}`}
              hideIfNoData
              additionalComponents={
                removeComments
                  ? undefined
                  : checkIsReport(
                      <AddComment
                        compId={`videos_video_view_${id}`}
                        id={videoId}
                      />,
                    )
              }
            />
          </ErrorBoundary>
        </Grid>
      ),
    })),
    {
      id: 'videos_video_view_trending_list',
      component:
        trendingListVideo?.length != 0 ? (
          <Grid item sm={12} md={6}>
            <ErrorBoundary>
              <SecondaryTable
                tableCardTitle={_t('videos:trending_list')}
                hideFilters
                hidePlatform
                columns={newTrendingListColumns}
                hideToggleView
                data={trendingListVideo}
                compId="videos_video_view_trending_list"
                customFilters={{ video_id: videoId }}
                expandUrlWithNavigate
                expandUrl={{
                  pathname: isTrending
                    ? path.trends.VIDEOS
                    : path.videos.VIDEOS_TABLE,
                  state: {
                    activeTab: 'trending',
                    option: {
                      label: currentVideo?.title,
                      value: +videoId,
                      isTrending: isTrending,
                    },
                    pageName: 'video_view',
                  },
                }}
                hideIfNoData
                additionalComponents={
                  removeComments
                    ? undefined
                    : checkIsReport(
                        <AddComment
                          compId={`videos_video_view_trending_list`}
                          id={videoId}
                        />,
                      )
                }
                isReport={isReport}
              />
            </ErrorBoundary>
          </Grid>
        ) : (
          <></>
        ),
    },
    {
      id: 'videos_video_view_most_frequent_words',
      component:
        currentVideo?.hashtags?.length != 0 ? (
          <Grid item sm={12} md={6}>
            <MostFrequentCard
              title={_t('videos:related_hashtags')}
              expandLink={`${path.hashtags.HASHTAG_VIEW_URI}`}
              words={currentVideo?.hashtags}
              compId="videos_video_view_most_frequent_words"
              moduleName={'HASHTAGS'}
              hideIfNoData
              isReport={isReport}
            />
          </Grid>
        ) : (
          <></>
        ),
    },
    {
      id: 'videos_video_view_related_tags',
      component:
        relatedTags?.length != 0 ? (
          <Grid item sm={12} md={6}>
            <MostFrequentCard
              title={_t('videos:related_tags')}
              words={relatedTags}
              compId="videos_video_view_related_tags"
              customFilters={{ video_id: videoId }}
              hideIfNoData
              additionalComponents={
                removeComments
                  ? undefined
                  : checkIsReport(
                      <AddComment
                        compId={`videos_video_view_related_tags`}
                        id={videoId}
                      />,
                    )
              }
              isReport={isReport}
            />
          </Grid>
        ) : (
          <></>
        ),
    },

    {
      id: 'videos-videoView-entitiesDetails',
      component: (
        <Grid item sm={12} className={styles.mb}>
          <ErrorBoundary>
            <CardTitle
              title={
                currentVideo?.influencer?.is_entity === false
                  ? _t('videos:influencer_details')
                  : _t('videos:entities_details')
              }
              showBorderBottom
              hideIcon
              maxFontSize
              compId="videos-videoView-entitiesDetails"
            />
          </ErrorBoundary>
        </Grid>
      ),
    },
    {
      id: 'basic-info',
      component: (
        <Grid item sm={12} className={styles.InfluencerDetails}>
          {influencerDetails.map(({ title, key }) => (
            <div className={styles.InfluencerDetails__Item} key={key}>
              <h5>{title}</h5>
              <span>
                {key == 'joined_at' ? (
                  dateTimeFormatter.date(currentVideo?.influencer?.[key])
                ) : key === 'name' ? (
                  <ToolTipComp
                    title={checkHoveringTranslated(
                      currentVideo?.influencer?.name?.original,
                      currentVideo?.influencer?.name?.arabic,
                    )}
                  >
                    <a
                      onClick={() =>
                        currentVideo?.influencer?.id
                          ? handelAuthorizationOnClick?.(currentVideo)
                          : openModalToAddinfluencer(
                              currentVideo?.channel?.remote_id,
                              currentVideo?.channel?.url,
                            )
                      }
                      className="cursor-pointer"
                    >
                      {checkTranslated(
                        currentVideo?.influencer?.name?.original,
                        currentVideo?.influencer?.name?.arabic,
                      ) || handleLongString(currentVideo?.channel_name, 45)}
                    </a>
                  </ToolTipComp>
                ) : key === 'address' ? (
                  currentVideo?.influencer?.address?.length ? (
                    currentVideo?.influencer?.[key]
                  ) : currentVideo?.channel?.country?.length ? (
                    currentVideo?.channel.country
                  ) : (
                    <span>-</span>
                  )
                ) : (
                  currentVideo?.influencer?.[key]
                )}
              </span>
            </div>
          ))}
        </Grid>
      ),
    },
    {
      id: 'videos_video_view_channels_details',
      component: (
        <Grid item sm={12} className={styles.mb}>
          <ErrorBoundary>
            <CardTitle
              title={_t('trends:channel_details')}
              showBorderBottom
              hideIcon
              maxFontSize
              compId="videos_video_view_channels_details"
            />
          </ErrorBoundary>
        </Grid>
      ),
    },
    ...channelIconCard.map(({ title, key, icon, id }) => ({
      id: `videos_video_view_${id}`,
      component: (
        <Grid
          item
          {...checkIsReport({ md: 3, lg: 6 }, { sm: 12, md: 3 })}
          key={key}
        >
          <ErrorBoundary>
            <IconCard
              title={title}
              key={key}
              withFilter
              hideInput
              isReport={isReport}
              cardData={{
                count: currentVideo?.channel?.[key],
                icon: icon,
              }}
              compId={`videos_video_view_${id}`}
              hideIfNoData
              additionalComponents={
                removeComments
                  ? undefined
                  : checkIsReport(
                      <AddComment
                        compId={`videos_video_view_${id}`}
                        id={videoId}
                      />,
                    )
              }
            />
          </ErrorBoundary>
        </Grid>
      ),
    })),
    {
      id: 'videos_video_view_channel_top_videos_table',
      component: (
        <Grid item sm={12} md={6}>
          <ErrorBoundary>
            <SecondaryTable
              tableCardTitle={_t('trends:channel_top_videos')}
              hideFilters
              hidePlatform
              columns={channelTopVideosColumn}
              hideToggleView
              data={currentVideo?.top_videos}
              compId="videos_video_view_channel_top_videos_table"
              hideIfNoData
              additionalComponents={
                removeComments
                  ? undefined
                  : checkIsReport(
                      <AddComment
                        compId={`videos_video_view_channel_top_videos_table`}
                        id={videoId}
                      />,
                    )
              }
              isReport={isReport}
            />
          </ErrorBoundary>
        </Grid>
      ),
    },
    ...baseLineData().map(({ id, title, slugName }: any) => ({
      id: `videos_video_view_${id}`,
      component: (
        <Grid item md={6} sm={12} key={id}>
          <ErrorBoundary>
            <BaseLine
              baseLineTitle={_t(`trends:${title}`)}
              slugName={slugName}
              trendsVideoChannelTooltip
              hideNames
              noDayInTimeInterval
              timeIntervalFilter
              customFilters={{ video_ids: [videoId] }}
              compId={`videos_video_view_${id}`}
              additionalComponents={
                removeComments
                  ? undefined
                  : checkIsReport(
                      <AddComment
                        compId={`videos_video_view_channels_details`}
                        id={videoId}
                      />,
                    )
              }
              isReport={isReport}
            />
          </ErrorBoundary>
        </Grid>
      ),
    })),
    {
      id: 'videos_video_view_channel_activity',
      component: (
        <Grid item md={6} sm={12}>
          <ErrorBoundary>
            <BaseLine
              baseLineTitle={_t('videos:channel_activity')}
              slugName={apiRoutes().channelActivities}
              showCustomTooltip
              hideNames
              noDayInTimeInterval
              timeIntervalFilter
              customFilters={{ video_ids: [videoId] }}
              compId={`videos_video_view_channel_activity`}
              additionalComponents={
                removeComments
                  ? undefined
                  : checkIsReport(
                      <AddComment
                        compId={`videos_video_view_channel_activity`}
                        id={videoId}
                      />,
                    )
              }
              isReport={isReport}
            />
          </ErrorBoundary>
        </Grid>
      ),
    },

    {
      id: 'videos_video_view_related_influencers',
      component: !!currentVideo?.related_influencers?.length ? (
        <Grid item lg={checkIsReport(6)} sm={6} md={3}>
          <ErrorBoundary>
            <SecondaryTable
              tableCardTitle={_t('videos:related_influencers')}
              hideFilters
              hidePlatform
              columns={relatedInfluencersColumns}
              data={currentVideo?.related_influencers}
              hideToggleView
              compId="videos_video_view_related_influencers"
              additionalComponents={
                removeComments
                  ? undefined
                  : checkIsReport(
                      <AddComment
                        compId={`videos_video_view_related_entities`}
                        id={videoId}
                      />,
                    )
              }
            />
          </ErrorBoundary>
        </Grid>
      ) : (
        <></>
      ),
    },
    {
      id: 'videos_video_view_related_entities',
      component: !!currentVideo?.related_entities?.length ? (
        <Grid item lg={checkIsReport(6)} sm={6} md={3}>
          <ErrorBoundary>
            <SecondaryTable
              tableCardTitle={_t('videos:related_entities')}
              hideFilters
              hidePlatform
              columns={relatedEntitiesColumns}
              data={currentVideo?.related_entities}
              hideToggleView
              compId="videos_video_view_related_entities"
              additionalComponents={
                removeComments
                  ? undefined
                  : checkIsReport(
                      <AddComment
                        compId={`videos_video_view_related_entities`}
                        id={videoId}
                      />,
                    )
              }
            />
          </ErrorBoundary>
        </Grid>
      ) : (
        <></>
      ),
    },
    {
      id: 'videos-videoView',
      component: (
        <Grid item sm={12}>
          <ErrorBoundary>
            <VideoDetails
              additionalComponents={
                removeComments
                  ? undefined
                  : checkIsReport(
                      <AddComment compId={`videos-videoView"`} id={videoId} />,
                    )
              }
              details={currentVideo}
              compId="videos-videoView"
            />
          </ErrorBoundary>
        </Grid>
      ),
    },
  ];

  const renderPage = () => {
    if (isReport) {
      const itemsSlugs = report?.values?.slugs || selectedSlugs;
      const chartsFilter = charts
        ?.filter((item) => itemsSlugs?.some((i: any) => i?.slug === item?.id))
        .map((item) => ({
          ...item,
          order: itemsSlugs.filter((i: any) => i?.slug === item?.id)?.[0]
            ?.order,
        }));

      const paragraph = itemsSlugs
        ?.map((item: any) =>
          !!item.paragraph
            ? {
                id: item.paragraph,
                order: item.order,
                component: (
                  <Paragraph
                    title={item.paragraph}
                    isTemplate={isTemplate}
                    key={item.paragraph}
                  />
                ),
              }
            : false,
        )
        .filter(Boolean);
      return [...chartsFilter, ...paragraph]
        .sort((a, b) => a.order - b.order)
        .map((item) => item.component);
    }
    return charts.map((chart, index) => (
      <Fragment key={index}>{chart.component}</Fragment>
    ));
  };
  if (!currentVideo) return <></>;
  return <>{renderPage()}</>;
};
